import type { Feature, LineString, MultiPolygon, Polygon, Position } from "geojson";
import { point } from "@turf/helpers";
import booleanPointInPolygon from "@turf/boolean-point-in-polygon";
import polygonToLine from "@turf/polygon-to-line";
import nearestPointOnLine from "@turf/nearest-point-on-line";
import distance from "@turf/distance";
import type { SamplePointCollection } from "../../sample/types";

export const MIN_SAFE_DISTANCE_METERS = 5;

/**
 * Check if a point is inside a polygon AND at least `safeDistanceMeters`
 * away from the nearest boundary edge. Handles Polygon and MultiPolygon.
 */
export function isPointSafeInsidePolygon(
	coord: Position,
	poly: Feature<Polygon | MultiPolygon>,
	safeDistanceMeters = MIN_SAFE_DISTANCE_METERS,
): boolean {
	if (poly.geometry.type === "MultiPolygon") {
		// Safe if safe in ANY sub-polygon
		return poly.geometry.coordinates.some((coords) => {
			const subPoly: Feature<Polygon> = {
				type: "Feature",
				properties: {},
				geometry: { type: "Polygon", coordinates: coords },
			};
			return isPointSafeInsideSinglePolygon(coord, subPoly, safeDistanceMeters);
		});
	}

	return isPointSafeInsideSinglePolygon(coord, poly as Feature<Polygon>, safeDistanceMeters);
}

function isPointSafeInsideSinglePolygon(coord: Position, poly: Feature<Polygon>, safeDistanceMeters: number): boolean {
	const pt = point(coord);

	if (!booleanPointInPolygon(pt, poly)) {
		return false;
	}

	// Convert polygon boundary to line(s)
	const line = polygonToLine(poly);
	const lineFeatures: Feature<LineString>[] = [];

	if (line.type === "FeatureCollection") {
		for (const f of line.features) {
			if (f.geometry.type === "LineString") {
				lineFeatures.push(f as Feature<LineString>);
			} else if (f.geometry.type === "MultiLineString") {
				for (const coords of f.geometry.coordinates) {
					lineFeatures.push({ type: "Feature", properties: {}, geometry: { type: "LineString", coordinates: coords } });
				}
			}
		}
	} else if (line.type === "Feature") {
		if (line.geometry.type === "LineString") {
			lineFeatures.push(line as Feature<LineString>);
		} else if (line.geometry.type === "MultiLineString") {
			for (const coords of line.geometry.coordinates) {
				lineFeatures.push({ type: "Feature", properties: {}, geometry: { type: "LineString", coordinates: coords } });
			}
		}
	}

	if (lineFeatures.length === 0) {
		return false;
	}

	// Find minimum distance to any boundary line
	let minDistKm = Infinity;
	for (const lf of lineFeatures) {
		const nearest = nearestPointOnLine(lf, pt);
		const d = distance(pt, nearest, { units: "kilometers" });
		if (d < minDistKm) {
			minDistKm = d;
		}
	}

	return minDistKm * 1000 > safeDistanceMeters;
}

/**
 * Find sample IDs that are NOT safe inside the boundary.
 * A point is unsafe if it's outside the boundary or within `safeDistanceMeters` of the edge.
 */
export function findUnsafeSampleIds(
	points: SamplePointCollection,
	boundary: Feature<Polygon | MultiPolygon>,
	safeDistanceMeters = MIN_SAFE_DISTANCE_METERS,
): Set<string> {
	const unsafe = new Set<string>();
	for (const f of points.features) {
		if (f.geometry.type !== "Point") continue;
		if (!isPointSafeInsidePolygon(f.geometry.coordinates, boundary, safeDistanceMeters)) {
			unsafe.add(f.properties.sample_id);
		}
	}
	return unsafe;
}
